import {  useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import TextOverflow from "utilities/TextOverflow";
import { FiCheck, FiChevronDown, FiChevronRight, FiPlus, FiX } from "react-icons/fi";
import { useEffect, useState } from "react";
import GuidesModal from "components/guides/GuidesModal";
import useApp from "hooks/useApp";
import { useOrganization } from "hooks/OrganizationContext";
import SmartImg from "assets/Smart.jpg";
import EnergyImg from "assets/Energy.jpg";
import CyberImg from "assets/CyberImg.jpg";
import DemoImage from "assets/Saudi-Logistic-HUB3.jpg";
import { CiLock } from "react-icons/ci";
import React from "react";
import ProjectActionMenu from "./ProjectActionMenu";
import MapNewUserModal from "./MapNewUserModal";
import { getSolutionUsersWithRoles, unmapUserFromSolution } from "api/solutions";

const ProjectListting: React.FC<any> = ({ data, isDisabled, setSelectedProject, productId, activeTab, type = "superadmin", title }) => {
  const [showGuidesModal, setShowGuidesModal] = useState(false);
  const [showAddNewButton, setShowAddNewButton] = useState(false);
  const [isShowBtnLoading, setIsShowBtnLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [teamSearch, setTeamSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [memberRole, setMemberRole] = useState<any>({});
  const [showMapNewUserModal, setShowMapNewUserModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string>("");
  const [projectUsers, setProjectUsers] = useState<{[projectId: string]: any[]}>({});
  const [loadingUsers, setLoadingUsers] = useState<{[projectId: string]: boolean}>({});

  const onEditClick = (project: any) => {
    // Safely parse coordinates if they exist, otherwise set to null
    let parsedCoordinates = null;
    if (project.coordinates && typeof project.coordinates === 'string') {
      try {
        parsedCoordinates = JSON.parse(project.coordinates);
      } catch (error) {
        console.warn('Failed to parse project coordinates:', error);
        parsedCoordinates = null;
      }
    }
    
    setSelectedProject({ ...project, coordinates: parsedCoordinates });
  };

  const { user } = useApp();
  const isAdmin = user?.role.includes('admin');
  const { selectedOrganization } = useOrganization();

  // Fetch users for a specific project
  const fetchProjectUsers = async (projectId: string) => {
    if (!selectedOrganization?.id || !projectId) return;
    
    setLoadingUsers(prev => ({ ...prev, [projectId]: true }));
    
    try {
      const response = await getSolutionUsersWithRoles(selectedOrganization.id, projectId);
      if (response.status === 200) {
        const users = response.data.list.map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          phone: user.phone || "N/A",
          status: user.status || "N/A",
          subscription: user.subscription || "N/A",
          role: user.roles, // Take first role if multiple
          roles: user.roles,
          dateJoined: user.dateJoined || "N/A", // API doesn't provide this, so we'll show N/A
          action: "Remove"
        }));
        
        setProjectUsers(prev => ({ ...prev, [projectId]: users }));
      } else {
        console.error('API returned non-200 status:', response.status);
        setProjectUsers(prev => ({ ...prev, [projectId]: [] }));
      }
    } catch (error) {
      console.error('Error fetching project users:', error);
      // Set empty array on error
      setProjectUsers(prev => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingUsers(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Handle row expansion and fetch users
  const toggleRowExpansion = async (projectId: string, project: any) => {
    // Don't allow expansion for Free Trial projects
    if (project.type === "FREE TRIAL") {
      return;
    }
    
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(projectId)) {
      newExpandedRows.delete(projectId);
    } else {
      newExpandedRows.add(projectId);
      // Fetch users when expanding
      if (!projectUsers[projectId]) {
        await fetchProjectUsers(projectId);
      }
    }
    setExpandedRows(newExpandedRows);
  };


  useEffect(() => {
    if (searchParams.get("more") === "true") {
      setShowGuidesModal(true);
    }
  }, [searchParams]);

  // Clear cached data and refetch when organization changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      // Clear cached project users when switching organizations
      setProjectUsers({});
      setLoadingUsers({});
      setExpandedRows(new Set());
      setTeamSearch("");
    }
  }, [selectedOrganization?.id]);

  useEffect(() => {
    setIsShowBtnLoading(false);
    const timeoutId = setTimeout(() => {
      const showAddNewProjectButton = isAdmin ? true :
        (data.filter((project: any) => (
         project.isEditable && (project.code !== "AAABBB" || project.code !== "AAACTY")
        )).length >= 1) || selectedOrganization.id === "0ed25137-caf2-42a0-bac0-4cd3e6b1bc8f" ? false : !isDisabled;

      setShowAddNewButton(showAddNewProjectButton);
      setIsShowBtnLoading(true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [isAdmin, data, selectedOrganization, isDisabled]);

  const getBackgroundImage = (category: string) => {
    switch (category?.toLowerCase()) {
      case "smart city":
        return SmartImg;
      case "energy":
        return EnergyImg;
      case "cyber security":
        return CyberImg;
      default:
        return DemoImage;
    }
  };

  const filteredData = () => {
    if (productId) {
      const staticData = data.filter((project: any) => project.productId === undefined);
      const filteredData = data.filter((project: any) => project.productId === productId);
      const allData = [ ...staticData, ...filteredData ];
      return allData;
    }
    return data;
  };

  // Helper function to get dummy data for unavailable fields
  const getDummyData = (project: any, field: string) => {
    const dummyData = {
      status: project.status || "Active",
      location: project.location || "Riyadh, Saudi Arabia",
      audience: project.audience || "Operational Excellence",
      organization: project.organization || "Saudi Aramco",
      budget: project.budget || "$500,000",
      startDate: project.startDate || "2024-01-15",
      endDate: project.endDate || "2024-12-31",
      progress: project.progress || "75%",
      teamSize: project.teamSize || "12 members"
    };
    return dummyData[field as keyof typeof dummyData] || "N/A";
  };

  // Open the Map New User modal
  const openMapNewUserModal = (projectId: string) => {
    setCurrentProjectId(projectId);
    setShowMapNewUserModal(true);
  };

  // Handle removing a user from a project
  const handleRemoveUser = async (projectId: string, userId: string, userName: string) => {
    if (!selectedOrganization?.id || !projectId || !userId) return;
    
    try {
      const response = await unmapUserFromSolution(selectedOrganization.id, projectId, { userId });
      if (response.status === 200) {
        // Refresh the user list for this project
        await fetchProjectUsers(projectId);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <div className="overflow-hidden overflow-y-auto ">
      {/* <div className="flex gap-2 items-center">
        <h1 className="text-base font-bold text-white">{title}</h1>
        {isLoading && <SmallestLoader />}
      </div> */}
      
      <div className="flex flex-col flex-grow gap-2 ">
        <div className="flex-grow">
          {/* Table Header */}
          <div className="sticky top-0 bg-[#232323] z-10">
            <div className="bg-[#2A2A2A] rounded-t-lg overflow-hidden">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                    <th className="text-left p-4 w-[15%]">Project Name</th>
                    {type === "superadmin" && <th className="text-left p-4 w-[15%]">Organization</th>}
                    {type === "user" && <th className="text-left p-4 w-[15%]"></th>}
                    <th className="text-left p-4 w-[12%]">Owner</th>
                    <th className="text-left p-4 w-[12%]">Date of creation</th>
                    <th className="text-left p-4 w-[15%]">Description</th>
                    <th className="text-left p-4 w-[10%]">Category</th>
                    <th className="text-right p-4 w-[10%]">Actions</th>
                  </tr>
                </thead>
              </table>
            </div>
          </div>

          {/* Table Body */}
          <div className="bg-[#2A2A2A] rounded-b-lg overflow-hidden">
            <table className="w-full table-fixed">
              <tbody>
                {filteredData().map((project: any, index: any) => {
                  // Determine if edit button should be shown
                  
                  const projectId = project.id || `project-${index}`;
                  const isExpanded = expandedRows.has(projectId);

                  return (
                    <React.Fragment key={index}>
                      <tr 
                        className={`border-b border-white/[0.08]  ${
                          project.type !== "FREE TRIAL" ? 'cursor-pointer' : 'cursor-default'
                        } ${isExpanded ? 'bg-[#cccccc33]' : ' hover:bg-[#3a3a3a33] '}`}
                        onClick={() => toggleRowExpansion(projectId, project)}
                      >
                        <td className="p-4 w-[15%]">
                          <div className="flex items-center gap-3">
                            {project.type !== "FREE TRIAL" ? (
                              <button 
                                className="text-gray-400 hover:text-white transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(projectId, project);
                                }}
                              >
                                {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                              </button>
                            ) : (
                              <div className="w-4 h-4"></div> // Spacer for Free Trial projects
                            )}
                            <div className="w-8 h-8 rounded-md bg-cover bg-center" 
                                 style={{ backgroundImage: `url(${getBackgroundImage(project.desc)})` }}>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium" 
                                      data-tooltip-id={`project-title-${index}`}
                                      data-tooltip-content={project.title}>
                                  {TextOverflow(project.title, 20)}
                                </span>
                                {project.status === "Private Project" && <CiLock className="text-gray-400" />}
                              </div>
                              <span className="text-xs text-gray-400">{project.type}</span>
                            </div>
                          </div>
                        </td>
                        {type === "superadmin" && <td className="p-4 w-[15%]">
                          {getDummyData(project, 'organization')}
                        </td>}
                        {type === "user" && <td className="p-4 w-[15%]">
                          
                        </td>}
                        <td className="p-4 w-[12%]">
                          Alex
                        </td>
                        <td className="p-4 w-[12%]">
                          10 Jun 2024
                        </td>
                        <td className="p-4 text-gray-400 w-[15%] max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                          Lorem ipsum dolor sit amet consectetur adipisicing elit. Illo maiores officia temporibus, eaque exercitationem quibusdam dolore ad alias, aperiam praesentium corrupti 
                        </td>
                        <td className="p-4 text-gray-400 w-[10%]">
                          {getDummyData(project, 'audience')}
                        </td>
                        <td className="p-4 text-right w-[10%]">
                          <ProjectActionMenu
                            link={project.link}
                            project={project}
                            onEdit={() => onEditClick(project)}
                            onDelete={() => {}}
                          />
                        </td>
                      </tr>

                      {/* Expanded Team Members Section */}
                      {isExpanded && (
                        <>
                          {/* Team Members Header Row */}
                          <tr className="border-b border-white/[0.08] bg-[#000]">
                            <td colSpan={7} className="p-4">
                              <div className="flex items-center justify-between">
                             <input type="text" placeholder="Search by name or email..." className="w-[200px] bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white" 
                             onChange={(e) => setTeamSearch(e.target.value)}
                             />
                                <button 
                                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMapNewUserModal(projectId);
                                  }}
                                >
                                  <FiPlus size={16} />
                                  <span className="text-sm">Map New user</span>
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Team Members Sub-table */}
                          <tr className="bg-[#000]">
                            <td colSpan={7} className="p-0">
                              <div className="bg-[#000]">
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-[#2A2A2A] text-gray-400 text-sm">
                                      <th className="text-left p-4 w-[10%]">Name</th>
                                      <th className="text-left p-4 w-[10%]">Email</th>
                                      <th className="text-left p-4 w-[10%]">Phone</th>
                                      <th className="text-left p-4 w-[10%]">Role</th>
                                      <th className="text-left p-4 w-[10%]">Date of Joined</th>
                                      <th className="text-left p-4 w-[10%]">Status</th>
                                      <th className="text-left p-4 w-[10%]">Subscription</th>
                                      <th className="text-right p-4 w-[10%]">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {loadingUsers[projectId] ? (
                                      <tr>
                                        <td colSpan={5} className="p-4 text-center text-gray-400">
                                          <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>Loading users...</span>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : projectUsers[projectId] && projectUsers[projectId].filter((member: any) => 
                                        member.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
                                        member.email.toLowerCase().includes(teamSearch.toLowerCase())
                                      ).length > 0 ? (
                                        projectUsers[projectId].filter((member: any) => 
                                          member.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
                                          member.email.toLowerCase().includes(teamSearch.toLowerCase())
                                        ).map((member: any) => (
                                          <tr key={member.id} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                                            <td className="p-4 w-[10%] text-white">
                                              {member.name}
                                            </td>
                                            {/* <td className="p-4 w-[20%] text-gray-400">
                                              <select className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                                                value={member.role}
                                                onChange={(e) => setMemberRole({ ...memberRole, [member.id]: e.target.value })}
                                              >
                                                <option value="OWNER">Owner</option>
                                                <option value="ADMIN">Admin</option>
                                                <option value="DEVELOPER">Developer</option>
                                                <option value="DESIGNER">Designer</option>
                                                <option value="USER">User</option>
                                              </select>
                                            </td> */}
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.email}
                                            </td>
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.phone}
                                            </td>
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.roles}
                                            </td>
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.dateJoined}
                                            </td>
                                            
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.status} {member.status === "ACTIVE" ? <FiCheck size={16} className="text-green-400" /> : <FiX size={16} className="text-red-400" />}
                                            </td>
                                            
                                            <td className="p-4 w-[10%] text-gray-400">
                                              {member.subscription}
                                            </td>
                                            <td className="p-4 text-right w-[10%]">
                                              {member.action && (
                                                <button 
                                                  className="text-red-400 hover:text-red-300 underline text-sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveUser(projectId, member.id, member.name);
                                                  }}
                                                >
                                                  {member.action}
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan={5} className="p-4 text-center text-gray-400">
                                            {projectUsers[projectId] && projectUsers[projectId].length === 0 
                                              ? "No users found for this project" 
                                              : "No users match your search"}
                                          </td>
                                        </tr>
                                      )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <GuidesModal
        activeTab={activeTab}
        isOpen={showGuidesModal}
        onClose={() => {
          setShowGuidesModal(false);
          searchParams.delete("more");
          setSearchParams(searchParams, { replace: true });
        }}
      />

      {/* Map New User Modal */}
      <MapNewUserModal
        isOpen={showMapNewUserModal}
        onClose={() => setShowMapNewUserModal(false)}
        projectId={currentProjectId}
        onUserMapped={() => {
          // Refresh the user list for the current project
          if (currentProjectId) {
            fetchProjectUsers(currentProjectId);
          }
        }}
      />

      <Tooltip
        id="need-help-tooltip"
        place="top"
        className="bg-[#1A1A1A] z-50 border border-white/[0.08] !opacity-100"
        style={{
          fontSize: '0.875rem',
          padding: '0.5rem 0.75rem'
        }}
      />

      {filteredData().map((project: any, index: any) => (
        <Tooltip
          key={`project-title-${index}`}
          id={`project-title-${index}`}
          place="top"
          className="bg-[#1A1A1A] z-50 border border-white/[0.08] !opacity-100"
          style={{
            fontSize: '0.875rem',
            padding: '0.5rem 0.75rem'
          }}
        />
      ))}
    </div>
  );
};

export default ProjectListting;